import React from 'react';
import throttle from 'lodash/throttle';
import { ReactStripeElements, injectStripe } from 'react-stripe-elements';
import { InjectedRouter, RouterState, PlainRoute } from 'react-router';
import { Location } from 'history';
import TutoringSelfServeViewPaymentLegalText from 'coaching/components/TutoringSelfServeView/TutoringSelfServeViewBilling/TutoringSelfServeViewBillingLegalText';
import TutoringSelfServeViewPaymentCard from 'coaching/components/TutoringSelfServeView/TutoringSelfServeViewBilling/TutoringSelfServeViewBillingCard';
import TutoringSelfServeViewPaymentSessionDetails from 'coaching/components/TutoringSelfServeView/TutoringSelfServeViewBilling/TutoringSelfServeViewBillingSessionDetails';
import CoachingSelfServeFailureView from 'coaching/components/CoachingSelfServeView/CoachingSelfServeFailureView';
import { confirmTutoringReservation } from 'coaching/actions/CoachingActions';
import { ConfirmTutoringReservationVariables } from 'coaching/mutations/__generated__/ConfirmTutoringReservation';
import CoachingHeading from 'coaching/components/CoachingHeading';
import { CoachingReservation } from 'coaching/containers/GetAvailableReservationsContainer';
import CoachingConstants from 'coaching/CoachingConstants';
import Row from 'components/Row';
import Copy from 'components/Copy';
import Text from 'components/Text';
import Button from 'components/Button';
import Link from 'components/Link';
import CurrentUser from 'auth/CurrentUser';
// @ts-ignore
import AuthActions from 'auth/AuthActions';
import { PurchaserRole } from 'globalTypes';
// @ts-ignore
import userAgent from 'lib/currentUserAgent';
import EventTracking from 'lib/EventTracking';
import { sendABTastyCustomEvent } from 'coaching/lib/abTasty';
import ToastActions from 'actions/ToastActions';
import {
  getReservationOrThrow,
  getUserTimezone,
  getPurchaserRole,
  resolveChildName,
  getStudentUuid,
  getGradeLevelOrThrow,
  getReservationUuidOrThrow,
  resolvePurchaserEmail,
  resolveFullName,
  clearStoredEmail,
  savePurchaseCompleted,
  savePurchaseResult,
  getSelectedSubjectOrThrow,
  throwIfEmpty,
} from 'lib/TutoringStateMachine/TutoringDataHelpers';
import { pushCustomEvent } from 'lib/googleTagManager';
import { shouldStripeErrorBeReported, reportStripeErrorToRollbar } from 'utils/selfServeHelpers';

export type Props = {
  router: InjectedRouter & RouterState;
  route: PlainRoute;
  goToNextState: () => void;
};

export type PurchaseFailureReason = 'PURCHASE_FAILURE_DUPLICATE_PURCHASE';

export type TutoringSelfServeViewBillingInternalState = {
  cardInputCompleted: boolean;
  submittingPayment: boolean;
  cardFailureReason?: string | null;
  // Non-Stripe purchase failures
  purchaseFailureReason?: PurchaseFailureReason;
};

export type ViewBillingInputData = {
  selectedSubject: string;
  selectedUserTimezone: string;
  selectedReservation: CoachingReservation;
  purchaserRole?: PurchaserRole;
  childName?: string;
  studentUuid?: string;
  gradeLevel: string;
  reservationUuid: string;
  email: string;
  fullName?: any;
};

export function retrieveDataForTutoringSelfServeViewBilling(): ViewBillingInputData {
  return {
    selectedSubject: getSelectedSubjectOrThrow(),
    selectedReservation: getReservationOrThrow(),
    selectedUserTimezone: getUserTimezone(),
    purchaserRole: getPurchaserRole(),
    childName: resolveChildName(),
    studentUuid: getStudentUuid(),
    gradeLevel: getGradeLevelOrThrow(),
    reservationUuid: getReservationUuidOrThrow(),
    email: resolvePurchaserEmail() || '',
    fullName: resolveFullName() || '',
  };
}

const legacyEventPrefix = 'coaching.self_schedule_purchase.review';
const eventPrefix = 'tutoring_self_schedule_purchase.review';

export type onSubmitPaymentParams = {
  selectedReservation: CoachingReservation;
  selectedSubject: string;
  selectedUserTimezone: string;
  purchaserRole?: PurchaserRole;
  childName?: string;
  studentUuid?: string;
  gradeLevel: string;
  reservationUuid: string;
  email: string;
  fullName?: any;
};

class TutoringSelfServeViewBilling extends React.Component<
  Props & ReactStripeElements.InjectedStripeProps,
  TutoringSelfServeViewBillingInternalState
> {
  state: TutoringSelfServeViewBillingInternalState = {
    cardInputCompleted: false,
    submittingPayment: false,
  };

  constructor(props: Props & ReactStripeElements.InjectedStripeProps) {
    super(props);
  }

  componentDidMount() {
    this.props.router.setRouteLeaveHook(this.props.route, (nextLocation: Location | undefined) => {
      if (nextLocation?.pathname === CoachingConstants.TUTORING_STUDENT_DETAILS) {
        EventTracking.trackKey(`${eventPrefix}.student_details.breadcrumb.click`, {
          student_name: resolveChildName() || '',
          tutoring_grade_level: getGradeLevelOrThrow(),
          tutoring_subject: getSelectedSubjectOrThrow(),
        });
      } else if (nextLocation?.pathname === CoachingConstants.TUTORING_SCHEDULE) {
        const reservation = getReservationOrThrow();
        EventTracking.trackKey(`${eventPrefix}.schedule.breadcrumb.click`, {
          reservation_uuid: getReservationUuidOrThrow(),
          coach_uuid: reservation.coachUuid,
          tutoring_subject: getSelectedSubjectOrThrow(),
          start_date_time: reservation.startDateTime.toISOString(),
          end_date_time: reservation.endDateTime.toISOString(),
        });
      }
    });
    sendABTastyCustomEvent('tutoring_billing');
    pushCustomEvent('coaching_payment_screen');
    EventTracking.trackKey(`${legacyEventPrefix}.view`);
  }

  onWrongEmailClick = (email: string | undefined) => async () => {
    EventTracking.trackKey(`${eventPrefix}.change_email.click`, {
      data: JSON.stringify({
        email,
      }),
    });

    clearStoredEmail();
    if (CurrentUser.isSignedIn()) {
      await AuthActions.signOut(CoachingConstants.TUTORING_GET_STARTED, true);
    } else {
      this.props.router.push(CoachingConstants.TUTORING_GET_STARTED);
    }
  };

  onHandleSubmitPayment = (params: onSubmitPaymentParams) => async () => {
    EventTracking.trackKey(`${legacyEventPrefix}.confirm.click`);
    const userIsSignedIn = CurrentUser.isSignedIn();
    let isStudent = userIsSignedIn ? CurrentUser.getUser()?.isStudent() : false;
    const {
      selectedUserTimezone,
      purchaserRole,
      childName,
      studentUuid,
      gradeLevel,
      reservationUuid,
      email,
      fullName,
    } = params;

    this.setState({ submittingPayment: true, cardFailureReason: null });

    const { source, error } = await this.props.stripe!.createSource({
      type: 'card',
      owner: fullName,
    });

    if (!source) {
      ToastActions.showErrorToast(
        error?.message ?? 'There was a problem saving your card. Please try again.'
      );
      console.warn(error?.message);
      if (error && shouldStripeErrorBeReported(error)) {
        reportStripeErrorToRollbar(
          'TutoringSelfServeViewBilling - CoachingStripe: There was an error creating the source.',
          error
        );
      }
      this.setState({ submittingPayment: false });
      return;
    }

    let payload: ConfirmTutoringReservationVariables = {
      reservationUuid,
      timezone: selectedUserTimezone,
      stripeSource: source.id,
      studentGradeLevel: gradeLevel,
    };

    if (userIsSignedIn && !isStudent) {
      if (studentUuid) {
        payload = {
          ...payload,
          studentUuid: studentUuid,
        };
      } else {
        payload = {
          ...payload,
          studentName: childName,
        };
      }
    }

    if (!userIsSignedIn && email && purchaserRole) {
      if (childName) {
        payload = {
          ...payload,
          studentName: childName,
          newUserAccount: {
            purchaserName: fullName,
            email,
            purchaserRole,
          },
        };
      } else {
        payload = {
          ...payload,
          newUserAccount: {
            purchaserName: fullName,
            email,
            purchaserRole,
          },
        };
      }
    }

    try {
      const confirmReservationResponse = await confirmTutoringReservation(payload);
      this.setState({ submittingPayment: false });
      if (confirmReservationResponse.error?.code === 'PURCHASE_FAILURE_CARD_FAILURE') {
        this.setState({ cardFailureReason: confirmReservationResponse.error.message });
        return;
      }

      if (confirmReservationResponse.error?.code === 'PURCHASE_FAILURE_DUPLICATE_PURCHASE') {
        this.setState({ purchaseFailureReason: 'PURCHASE_FAILURE_DUPLICATE_PURCHASE' });
        return;
      }

      // Any other unknown error goes to the catch-all error screen
      if (confirmReservationResponse.error || confirmReservationResponse.participantUuid === null) {
        console.warn(
          'TutoringSelfServeViewBilling - purchase failed',
          confirmReservationResponse.error
        );
        ToastActions.showErrorToast(
          confirmReservationResponse.error?.message ||
            'There was a problem with the purchase. Please try again.'
        );
        return;
      }

      // Track success in GTM via custom trigger
      pushCustomEvent('free_session_signup_completed');

      const { participantUuid, addStudentToken } = confirmReservationResponse;

      savePurchaseCompleted(true);
      savePurchaseResult({
        participantUUID: throwIfEmpty(participantUuid),
        linkStudentToken: addStudentToken,
      });

      this.props.goToNextState();
    } catch (error) {
      console.warn(error);
      ToastActions.showErrorToast(`${error}`);
      this.setState({ submittingPayment: false });
    }
  };

  render() {
    const {
      selectedReservation,
      selectedSubject,
      selectedUserTimezone,
      purchaserRole,
      childName,
      studentUuid,
      gradeLevel,
      reservationUuid,
      email,
      fullName,
    } = retrieveDataForTutoringSelfServeViewBilling();
    const userIsSignedIn = CurrentUser.isSignedIn();
    const isStudent = userIsSignedIn ? CurrentUser.getUser()?.isStudent() : false;
    const Container = userAgent.isDesktop() ? React.Fragment : Row;
    const submitPaymentParams = {
      selectedUserTimezone,
      purchaserRole,
      childName,
      studentUuid,
      gradeLevel,
      reservationUuid,
      email,
      fullName,
      selectedSubject,
      selectedReservation,
    };

    if (this.state.purchaseFailureReason) {
      return <CoachingSelfServeFailureView failureReason={this.state.purchaseFailureReason} />;
    }

    return (
      <React.Fragment>
        <CoachingHeading data-test="billing-heading" size="small">
          Let’s book that free session
        </CoachingHeading>
        <Row size="small">
          <Copy data-test="billing-header-copy" align="left" size="small">
            After you try your free first session, you can continue with your tutor (or a different
            tutor) at <Text weight="semibold">$40 per session</Text>. Skipping, rescheduling, or
            canceling sessions is easy.
          </Copy>
        </Row>
        <TutoringSelfServeViewPaymentSessionDetails
          studentName={childName}
          selectedSubject={selectedSubject}
          reservation={selectedReservation}
          userTimezone={selectedUserTimezone}
        />
        <TutoringSelfServeViewPaymentCard
          onSetCardFailureReason={(failureReason?: string | null) =>
            this.setState({ cardFailureReason: failureReason })
          }
          onCardInputCompleteChange={cardInputCompleted => this.setState({ cardInputCompleted })}
          cardFailureReason={this.state.cardFailureReason}
        />
        <Row size="small">
          We’ll send confirmation to:{' '}
          <Container>
            <Text weight="bold">{email}</Text>{' '}
            <Link onClick={this.onWrongEmailClick(email)} data-test="billing-wrong-email">
              Wrong email?
            </Link>
          </Container>
        </Row>
        <Row size="large">
          <Button
            fullAtBreakpoint="480"
            data-test="billing-confirm-button"
            color="koolaid"
            onClick={throttle(this.onHandleSubmitPayment(submitPaymentParams), 1000)}
            loading={this.state.submittingPayment}
            disabled={!this.state.cardInputCompleted}>
            Confirm
          </Button>
        </Row>
        <TutoringSelfServeViewPaymentLegalText
          isStudent={isStudent}
          purchaseForConnectedChild={false}
        />
      </React.Fragment>
    );
  }
}

export { TutoringSelfServeViewBilling };

export default injectStripe(TutoringSelfServeViewBilling);
